import { useMemo } from "react";

import { invoiceStatusId } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceStatus } from "~/modules/billing/api/invoiceStatus/invoiceStatusIdTypes.ts";
import type { InvoiceAction, InvoiceStatusId } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import InvoiceStatusProgressSegment
	from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceStatusProgressBar/components/InvoiceStatusProgressSegment.tsx";
import type {
	InvoiceStatusUpdateAction
} from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceStatusProgressBar/invoiceStatusProgressBarTypes.ts";
import {
	getRevertBlockedReason,
	getSegmentTheme,
	getTooltipTextForSegment,
} from "~/modules/billing/components/InvoiceDetailsView/components/InvoiceStatusProgressBar/invoiceStatusProgressBarUtils.ts";


type Props = {
	currentStatusId: InvoiceStatusId;
	isSelfBilledInvoice: boolean;
	onStatusClick?: (statusId: InvoiceStatusId, action: InvoiceStatusUpdateAction) => void;
	recipientIsSet: boolean;
	revertAction: InvoiceAction;
	revertBeforeSentAction: InvoiceAction;
	statuses: InvoiceStatus[];
};

export default function InvoiceStatusProgressBar({
	currentStatusId,
	isSelfBilledInvoice,
	onStatusClick,
	recipientIsSet,
	revertAction,
	revertBeforeSentAction,
	statuses,
}: Props) {
	const visibleStatuses = useMemo(() => {
		const sorted = [...statuses].filter(status => status.id !== invoiceStatusId.Pending).sort((a,
			b) => a.order - b.order);
		if (currentStatusId === invoiceStatusId.Reversed) {
			return sorted;
		}
		return sorted.filter((s) => s.id !== invoiceStatusId.Reversed);
	}, [statuses, currentStatusId]);

	const currentOrder = useMemo(
		() => statuses.find((s) => s.id === currentStatusId)?.order ?? 0,
		[statuses, currentStatusId],
	);

	return (
		<div className="flex w-full rounded-md bg-white">
			{visibleStatuses.map((status, index) => {
				const theme = getSegmentTheme({
					currentInvoiceStatusId: currentStatusId,
					currentStatusOrderPosition: currentOrder,
					isSelfBilledInvoice,
					recipientIsSet,
					status,
				});

				const segmentTooltipText = getTooltipTextForSegment({
					currentStatusId,
					isSelfBilledInvoice,
					statusId: status.id,
					theme,
				});

				let action:InvoiceStatusUpdateAction = "advance";

				if (status.order < currentOrder) {
					action = "revert";
				}

				const revertBlockedReason = action === "revert"
					? getRevertBlockedReason({ revertAction, revertBeforeSentAction, targetStatusId: status.id })
					: null;
				const isClickable = !!onStatusClick && revertBlockedReason === null;

				const toolTipText = revertBlockedReason || segmentTooltipText;

				return (
					<InvoiceStatusProgressSegment
						key={status.id}
						onClick={isClickable ? () => onStatusClick(status.id, action) : undefined}
						status={status}
						theme={theme}
						isFirst={index === 0}
						isLast={index === visibleStatuses.length - 1}
						isSelfBilledInvoice={isSelfBilledInvoice}
						tooltip={toolTipText}
					/>
				);
			})}
		</div>
	);
}